import React, { useEffect, useState } from 'react'
import { Button, ConfigProvider, Input } from 'antd'
import styled, { keyframes } from 'styled-components'

import logo from '@public/logo.svg'
import { LottiePlayer } from '@components/common/LottiePlayer'
import errorAnimation from 'src/lotties/error-oops.json'
import { theme } from 'src/utils/theme'
import { captureMessage, getClient, setTag } from './buffer'

type Props = {
  reference: string
  onRetry: () => void
}

type FeedbackIntegration = { openDialog: () => void }

const getFeedback = (): FeedbackIntegration | undefined =>
  getClient()?.getIntegrationByName?.('Feedback') as
    | FeedbackIntegration
    | undefined

// Full-screen error screen shown by the global ErrorBoundary.
// Design brief: short, never technical, two actions, one discreet reference —
// wrapped in a calm, on-brand layout with a friendly looping animation.
const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: radial-gradient(
      1200px 600px at 50% -10%,
      #ffffff 0%,
      rgba(255, 255, 255, 0) 60%
    ),
    linear-gradient(160deg, #ecf8f6 0%, #e3f1ef 55%, #dfeeeb 100%);
`

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: #ffffff;
  border-radius: 24px;
  padding: 40px 36px 32px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04),
    0 24px 48px -12px rgba(16, 24, 40, 0.18);
  border: 1px solid rgba(84, 128, 82, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  animation: ${fadeUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
`

const Logo = styled.img`
  height: 32px;
  width: auto;
  align-self: center;
  opacity: 0.9;
`

const Animation = styled.div`
  width: 168px;
  height: 168px;
  margin: 4px 0 -4px;
`

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1f2937;
`

const Body = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: #4b5563;
  max-width: 38ch;
`

const Reference = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: #9ca3af;
`

const Code = styled.code`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #548052;
  background: rgba(84, 128, 82, 0.08);
  padding: 2px 8px;
  border-radius: 6px;
  letter-spacing: 0.04em;
`

const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 12px;
  width: 100%;
`

const MessageField = styled(Input.TextArea)`
  margin-top: 8px;
  text-align: left;
`

const Confirmation = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.55;
  color: #548052;
  background: rgba(84, 128, 82, 0.08);
  padding: 12px 16px;
  border-radius: 12px;
`

export const ErrorFallback: React.FC<Props> = ({ reference, onRetry }) => {
  // The native Sentry Feedback dialog is the primary "add a message" path
  // (same widget as the sidebar bug button). It needs the lazily-loaded SDK;
  // when it isn't available (dev, or SDK not yet bound) we fall back to an
  // inline textarea that captures the message through the eager buffer.
  const [feedbackAvailable, setFeedbackAvailable] = useState(false)
  const [showMessage, setShowMessage] = useState(false)
  const [text, setText] = useState('')
  const [sent, setSent] = useState(false)

  useEffect(() => {
    setFeedbackAvailable(Boolean(getFeedback()))
  }, [])

  const openSupport = (): void => {
    const feedback = getFeedback()
    if (feedback) {
      // Correlate the feedback event with this crash. Cleared on dialog close
      // by the integration's onFormClose (see sentry/init.ts).
      setTag('support_reference', reference)
      setTag('error_source', 'error_boundary')
      feedback.openDialog()
      return
    }
    setShowMessage(true)
  }

  const sendInlineMessage = (): void => {
    const comment = text.trim()
    captureMessage(
      comment
        ? `Message support (réf ${reference}): ${comment}`
        : `Demande de support (réf ${reference})`,
      {
        level: 'info',
        tags: { support_reference: reference, source: 'error_boundary' },
        ctx: { user_message: { reference, message: comment || null } },
      },
    )
    setSent(true)
  }

  return (
    <ConfigProvider theme={theme}>
      <Page>
        <Card>
          <Logo src={logo} alt="autoriz" />
          <Animation>
            <LottiePlayer
              autoplay
              loop
              src={errorAnimation}
              style={{ height: '100%', width: '100%' }}
              rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
            />
          </Animation>
          <Title>Erreur inattendue</Title>
          <Body>
            Quelque chose s&apos;est mal passé de notre côté. Vos projets et vos
            documents sont en sécurité — rien n&apos;a été perdu.
          </Body>
          <Body>
            Vous pouvez réessayer maintenant. Si le problème continue,
            écrivez-nous, on s&apos;en occupe.
          </Body>

          {sent ? (
            <>
              <Confirmation>
                Merci — votre message a bien été transmis. Notre équipe va
                regarder.
              </Confirmation>
              <Actions>
                <Button type="primary" onClick={onRetry}>
                  Réessayer
                </Button>
              </Actions>
            </>
          ) : (
            <>
              {showMessage && !feedbackAvailable && (
                <MessageField
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Décrivez ce que vous faisiez (facultatif)"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  maxLength={1000}
                  autoFocus
                />
              )}

              <Actions>
                <Button type="primary" onClick={onRetry}>
                  Réessayer
                </Button>
                {showMessage && !feedbackAvailable ? (
                  <Button type="default" onClick={sendInlineMessage}>
                    Envoyer
                  </Button>
                ) : (
                  <Button type="default" onClick={openSupport}>
                    Envoyer au support
                  </Button>
                )}
              </Actions>
            </>
          )}

          <Reference>
            Référence <Code>{reference}</Code> — à transmettre si vous nous
            écrivez
          </Reference>
        </Card>
      </Page>
    </ConfigProvider>
  )
}
